import React from 'react';
import { motion } from 'framer-motion';
export type SlideId = 'opening' | 'experienced' | 'model' | 'journey' | 'snakify' | 'channels' | 'conversation' | 'signals' | 'campaign' | 'learning' | 'peer' | 'execution' | 'closing';
export interface SlideProps {
  index: number;
  total: number;
  /** Jump to another slide by id (not provided in PDF export). */
  onNavigate?: (id: SlideId) => void;
}
interface SlideTitleProps {
  eyebrow: string;
  children: React.ReactNode;
  sub?: React.ReactNode;
}

/** Interior-slide header: thin-ruled eyebrow + large, confident headline. */
export function SlideTitle({
  eyebrow,
  children,
  sub
}: SlideTitleProps) {
  return <motion.header initial={{
    opacity: 0,
    y: 16
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.5,
    ease: [0.23, 1, 0.32, 1]
  }} className="flex-shrink-0" data-id="element-532" data-mp="2vx2z5teei3ri">
      <div className="flex items-center gap-3" data-id="element-533" data-mp="238du5ja85eah">
        <span className="h-px w-8 bg-[var(--brand)]" aria-hidden="true" data-id="element-534" data-mp="237m9v4m2v2zi" />
        <p className="text-[clamp(0.62rem,0.85vw,0.78rem)] font-bold uppercase tracking-[0.22em] text-[var(--brand)]" data-id="element-535" data-mp="1ie7e71crisik">{eyebrow}</p>
      </div>
      <h2 className="mt-[clamp(0.6rem,1.2vw,1rem)] max-w-5xl text-[clamp(1.9rem,3.8vw,3.6rem)] font-extrabold leading-[1.02] tracking-[-0.045em] text-[var(--fg)]" data-id="element-536" data-mp="1mcwscw5mke39">
        {children}
      </h2>
      {sub && <p className="mt-[clamp(0.5rem,1vw,0.85rem)] max-w-2xl text-[clamp(0.9rem,1.25vw,1.15rem)] font-medium leading-snug text-[var(--muted)]" data-id="element-537" data-mp="1w6nbrmmw9yc3">
          {sub}
        </p>}
    </motion.header>;
}
interface ClosingLineProps {
  children: React.ReactNode;
  delay?: number;
  large?: boolean;
}

/** Closing statement under a thin rule — the one line the audience should remember. */
export function ClosingLine({
  children,
  delay = 0.7,
  large = false
}: ClosingLineProps) {
  return <motion.div initial={{
    opacity: 0,
    y: 12
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay,
    duration: 0.5
  }} className="mt-[clamp(0.9rem,1.8vw,1.5rem)] flex-shrink-0 border-t border-[var(--fg)]/10 pt-[clamp(0.8rem,1.5vw,1.25rem)]" data-id="element-538" data-mp="2jwho833axkvt">
      <p className={`font-extrabold leading-tight tracking-[-0.03em] text-[var(--fg)] ${large ? 'text-[clamp(1.6rem,3.2vw,3rem)]' : 'text-[clamp(1.05rem,1.8vw,1.65rem)]'}`} data-id="element-539" data-mp="1u5gdvq4m3vxt">
        {children}
      </p>
    </motion.div>;
}
interface InteriorSlideProps {
  surface?: boolean;
  children: React.ReactNode;
}

/** Full-screen interior slide. Content sits in a padded column engineered to fit one screen. */
export function InteriorSlide({
  surface = false,
  children
}: InteriorSlideProps) {
  return <div className={`relative flex h-full w-full flex-col overflow-hidden ${surface ? 'bg-[var(--surface)]' : 'bg-[var(--bg)]'}`} data-id="element-540" data-mp="1q6hai9yj478h">
      <div className="flex min-h-0 flex-1 flex-col px-[clamp(2rem,5vw,5rem)] pb-[clamp(3.25rem,4.5vw,4rem)] pt-[clamp(3.25rem,5vw,4.5rem)]" data-id="element-541" data-mp="xntqkxv9qy8z">
        {children}
      </div>
    </div>;
}